// Role Policy Editor Component - Define policy statements for a role

import { Plus, Shield, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import type { Action, Resource } from './types';

type PolicyStatement = {
  resourceId: string;
  actionId: string;
  effect: 'Allow' | 'Deny';
  priority: number;
  rowCondition?: Record<string, unknown>;
  columnCondition?: {
    whitelist?: string[];
    blacklist?: string[];
  };
  timeCondition?: Record<string, unknown>;
  contextCondition?: Record<string, unknown>;
};

type RolePolicyEditorProps = {
  resources: Resource[];
  actions: Action[];
  tenantId: string;
  initialStatements?: PolicyStatement[];
  onStatementsChange: (statements: PolicyStatement[]) => void;
};

export const RolePolicyEditor: React.FC<RolePolicyEditorProps> = ({
  resources,
  actions,
  tenantId,
  initialStatements = [],
  onStatementsChange,
}) => {
  const [statements, setStatements] = useState<PolicyStatement[]>(initialStatements);
  const [selectedResource, setSelectedResource] = useState<string>('');
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [selectedEffect, setSelectedEffect] = useState<'Allow' | 'Deny'>('Allow');
  const [priority, setPriority] = useState<number>(100);
  const [rowConditionJson, setRowConditionJson] = useState<string>('{}');
  const [columnWhitelist, setColumnWhitelist] = useState<string>('');
  const [columnBlacklist, setColumnBlacklist] = useState<string>('');

  // Sync statements when initialStatements change
  useEffect(() => {
    setStatements(initialStatements);
  }, [initialStatements]);

  const addStatement = () => {
    if (!selectedResource || !selectedAction) {
      alert('Please select a resource and action');
      return;
    }

    let rowCondition: Record<string, unknown> | undefined;
    try {
      rowCondition = JSON.parse(rowConditionJson);
    } catch (e) {
      alert('Invalid JSON for row condition');
      return;
    }

    const columnCondition: {
      whitelist?: string[];
      blacklist?: string[];
    } = {};
    if (columnWhitelist.trim()) {
      columnCondition.whitelist = columnWhitelist.split(',').map((s) => s.trim()).filter(Boolean);
    }
    if (columnBlacklist.trim()) {
      columnCondition.blacklist = columnBlacklist.split(',').map((s) => s.trim()).filter(Boolean);
    }

    const newStatement: PolicyStatement = {
      resourceId: selectedResource,
      actionId: selectedAction,
      effect: selectedEffect,
      priority,
      rowCondition: Object.keys(rowCondition).length > 0 ? rowCondition : undefined,
      columnCondition: Object.keys(columnCondition).length > 0 ? columnCondition : undefined,
    };

    const updatedStatements = [...statements, newStatement];
    setStatements(updatedStatements);
    onStatementsChange(updatedStatements);
    
    // Reset form
    setSelectedResource('');
    setSelectedAction('');
    setSelectedEffect('Allow');
    setPriority(100);
    setRowConditionJson('{}');
    setColumnWhitelist('');
    setColumnBlacklist('');
  };

  const removeStatement = (index: number) => {
    const updatedStatements = statements.filter((_, i) => i !== index);
    setStatements(updatedStatements);
    onStatementsChange(updatedStatements);
  };

  const getResourceName = (resourceId: string) => {
    return resources.find((r) => r.resourceId === resourceId)?.resourceName || resourceId;
  };

  const getActionName = (actionId: string) => {
    return actions.find((a) => a.actionId === actionId)?.actionName || actionId;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Policy Statements</CardTitle>
        <CardDescription>
          Define granular permissions for resources and actions
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add Statement Form */}
        <div className="border rounded-lg p-4 space-y-4">
          <h3 className="font-semibold">Add Policy Statement</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="resource-select">Resource *</Label>
              <Select value={selectedResource} onValueChange={setSelectedResource}>
                <SelectTrigger id="resource-select">
                  <SelectValue placeholder="Select resource" />
                </SelectTrigger>
                <SelectContent>
                  {resources.map((resource) => (
                    <SelectItem key={resource.resourceId} value={resource.resourceId}>
                      {resource.resourceName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="action-select">Action *</Label>
              <Select value={selectedAction} onValueChange={setSelectedAction}>
                <SelectTrigger id="action-select">
                  <SelectValue placeholder="Select action" />
                </SelectTrigger>
                <SelectContent>
                  {actions.map((action) => (
                    <SelectItem key={action.actionId} value={action.actionId}>
                      {action.actionName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="effect-select">Effect *</Label>
              <Select value={selectedEffect} onValueChange={(v) => setSelectedEffect(v as 'Allow' | 'Deny')}>
                <SelectTrigger id="effect-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Allow">Allow</SelectItem>
                  <SelectItem value="Deny">Deny</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="priority">Priority</Label>
              <Input
                id="priority"
                type="number"
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value) || 100)}
                min={0}
                max={1000}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="row-condition">Row Condition (JSON)</Label>
            <Textarea
              id="row-condition"
              value={rowConditionJson}
              onChange={(e) => setRowConditionJson(e.target.value)}
              placeholder='{"field": "value"}'
              rows={3}
              className="font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground mt-1">
              JSON object defining row-level conditions (e.g., {"{"}"owner_id": "user_id"{"}"})
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="column-whitelist">Column Whitelist</Label>
              <Input
                id="column-whitelist"
                value={columnWhitelist}
                onChange={(e) => setColumnWhitelist(e.target.value)}
                placeholder="col1, col2, col3"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Comma-separated list of allowed columns
              </p>
            </div>
            <div>
              <Label htmlFor="column-blacklist">Column Blacklist</Label>
              <Input
                id="column-blacklist"
                value={columnBlacklist}
                onChange={(e) => setColumnBlacklist(e.target.value)}
                placeholder="col1, col2, col3"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Comma-separated list of restricted columns
              </p>
            </div>
          </div>
          <Button onClick={addStatement} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Statement
          </Button>
        </div>

        {/* Statements List */}
        {statements.length > 0 && (
          <div className="space-y-2">
            <h3 className="font-semibold">Policy Statements ({statements.length})</h3>
            {statements.map((statement, index) => (
              <div
                key={index}
                className="border rounded-lg p-4 flex items-start justify-between"
              >
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={statement.effect === 'Allow' ? 'default' : 'destructive'}>
                      {statement.effect}
                    </Badge>
                    <span className="font-medium">{getResourceName(statement.resourceId)}</span>
                    <span className="text-muted-foreground">→</span>
                    <span>{getActionName(statement.actionId)}</span>
                    <Badge variant="outline">Priority: {statement.priority}</Badge>
                  </div>
                  {statement.rowCondition && (
                    <div className="text-xs text-muted-foreground">
                      Row Condition: {JSON.stringify(statement.rowCondition)}
                    </div>
                  )}
                  {statement.columnCondition && (
                    <div className="text-xs text-muted-foreground">
                      {statement.columnCondition.whitelist && (
                        <span>Whitelist: {statement.columnCondition.whitelist.join(', ')}</span>
                      )}
                      {statement.columnCondition.blacklist && (
                        <span>Blacklist: {statement.columnCondition.blacklist.join(', ')}</span>
                      )}
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeStatement(index)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {statements.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No policy statements defined yet</p>
            <p className="text-sm">Add statements above to define permissions</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

